import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building, Upload, Wand2, Edit3, Package, Star,
  ChevronLeft, ChevronRight, Check, X, Plus,
  Sparkles, FileText, Tag, Euro, Calendar,
  Globe, Users, BarChart3, Award, MessageSquare,
  Video, Image as ImageIcon, ThumbsUp, Eye, ArrowLeft
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CreateClareefaiPage: React.FC = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Étape 1: Identité de l'entreprise
    logo: '',
    companyName: '',
    slogan: '',
    sectors: [] as string[],
    employeeCount: '',
    
    // Étape 2: Description
    useAI: false,
    description: '',
    foundedDate: '',
    
    // Étape 3: Solutions
    solutions: [] as Array<{
      id: string;
      name: string;
      pitch: string;
      tags: string[];
      price: string;
    }>,
    
    // Étape 4: Reviews
    googleReviewLink: '',
    featuredReviews: [] as Array<{
      id: string;
      author: string;
      company: string;
      rating: number;
      comment: string;
      product: string;
      sector: string;
      companySize: string;
      hasVideo: boolean;
      hasImage: boolean;
      companyResponse?: string;
    }>,
    satisfactionRates: {
      quality: 95,
      support: 92,
      easeOfUse: 88,
      valueForMoney: 90,
      recommendation: 94
    }
  });

  const sectors = [
    'Technology', 'Finance', 'Healthcare', 'Education', 'Commerce', 'Industry',
    'Services', 'Real Estate', 'Transport', 'Energy', 'Media', 'Consulting',
    'Marketing', 'HR', 'Legal', 'Agriculture', 'Tourism', 'Sports'
  ];

  const employeeCounts = [
    { value: '1-10 employees', label: t('createPage.step1.employees.1_10') },
    { value: '11-50 employees', label: t('createPage.step1.employees.11_50') },
    { value: '51-200 employees', label: t('createPage.step1.employees.51_200') },
    { value: '201-500 employees', label: t('createPage.step1.employees.201_500') },
    { value: '501-1000 employees', label: t('createPage.step1.employees.501_1000') },
    { value: '1000+ employees', label: t('createPage.step1.employees.1000plus') },
  ];

  const availableTags = [
    'AI', 'Automation', 'CRM', 'Analytics', 'Cloud', 'Mobile',
    'SaaS', 'API', 'Integration', 'Security', 'Performance', 'Innovation'
  ];

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSectorToggle = (sector: string) => {
    setFormData(prev => ({
      ...prev,
      sectors: prev.sectors.includes(sector)
        ? prev.sectors.filter(s => s !== sector)
        : [...prev.sectors, sector]
    }));
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    // Sauvegarder les données dans localStorage
    localStorage.setItem('clareefai_page_data', JSON.stringify(formData));
    
    // Rediriger vers la page de login ou afficher un message de succès
    window.location.href = '/login';
  };

  const generateAIDescription = () => {
    const sectorsPart = formData.sectors.length > 0
      ? t('createPage.step2.specializedIn', { sectors: formData.sectors.slice(0, 2).join(' and ') })
      : t('createPage.step2.marketLeader');
    const employeeCountLower = formData.employeeCount
      ? formData.employeeCount.toLowerCase()
      : t('createPage.step2.experienced');
    const missionPart = formData.slogan
      ? t('createPage.step2.missionQuote', { slogan: formData.slogan })
      : t('createPage.step2.missionDefault');

    const aiDescription = t('createPage.step2.aiDescriptionTemplate', {
      companyName: formData.companyName,
      sectorsPart,
      employeeCountLower,
      missionPart,
    });

    setFormData(prev => ({
      ...prev,
      description: aiDescription
    }));
  };

  const addSolution = () => {
    const newSolution = {
      id: Date.now().toString(),
      name: '',
      pitch: '',
      tags: [],
      price: ''
    };
    setFormData(prev => ({
      ...prev,
      solutions: [...prev.solutions, newSolution]
    }));
  };

  const updateSolution = (id: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      solutions: prev.solutions.map(solution =>
        solution.id === id ? { ...solution, [field]: value } : solution
      )
    }));
  };

  const removeSolution = (id: string) => {
    setFormData(prev => ({
      ...prev,
      solutions: prev.solutions.filter(solution => solution.id !== id)
    }));
  };

  const addFeaturedReview = () => {
    const newReview = {
      id: Date.now().toString(),
      author: '',
      company: '',
      rating: 5,
      comment: '',
      product: '',
      sector: '',
      companySize: '',
      hasVideo: false,
      hasImage: false,
      companyResponse: ''
    };
    setFormData(prev => ({
      ...prev,
      featuredReviews: [...prev.featuredReviews, newReview]
    }));
  };

  const updateReview = (id: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      featuredReviews: prev.featuredReviews.map(review =>
        review.id === id ? { ...review, [field]: value } : review
      )
    }));
  };

  const removeReview = (id: string) => {
    setFormData(prev => ({
      ...prev,
      featuredReviews: prev.featuredReviews.filter(review => review.id !== id)
    }));
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((step) => (
        <React.Fragment key={step}>
          <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 font-semibold ${
            step <= currentStep 
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 border-blue-600 text-white' 
              : 'border-gray-300 text-gray-400'
          }`}>
            {step < currentStep ? <Check className="w-6 h-6" /> : step}
          </div>
          {step < 4 && (
            <div className={`w-20 h-0.5 ${
              step < currentStep ? 'bg-gradient-to-r from-blue-600 to-purple-600' : 'bg-gray-300'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('createPage.step1.title')}</h3>
        <p className="text-gray-600">{t('createPage.step1.description')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Logo */}
        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <Upload className="w-4 h-4 inline mr-2" />
            {t('createPage.step1.companyLogo')}
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition-colors">
            {formData.logo ? (
              <div className="space-y-4">
                <img src={formData.logo} alt="Logo" className="h-20 mx-auto" />
                <button
                  onClick={() => handleChange('logo', '')}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  {t('createPage.step1.remove')}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                <div>
                  <p className="text-gray-600">{t('createPage.step1.dragDropLogo')}</p>
                  <button
                    onClick={() => handleChange('logo', '/logo-color-favicone.png')}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {t('createPage.step1.useDefaultLogo')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Company name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Building className="w-4 h-4 inline mr-1" />
            {t('createPage.step1.companyName')}
          </label>
          <input
            type="text"
            value={formData.companyName}
            onChange={(e) => handleChange('companyName', e.target.value)}
            className="form-input"
            placeholder={t('createPage.step1.companyNamePlaceholder')}
            required
          />
        </div>

        {/* Slogan */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Sparkles className="w-4 h-4 inline mr-1" />
            {t('createPage.step1.slogan')}
          </label>
          <input
            type="text"
            value={formData.slogan}
            onChange={(e) => handleChange('slogan', e.target.value)}
            className="form-input"
            placeholder={t('createPage.step1.sloganPlaceholder')}
          />
        </div>

        {/* Employee count */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Users className="w-4 h-4 inline mr-1" />
            {t('createPage.step1.employeeCount')}
          </label>
          <select
            value={formData.employeeCount}
            onChange={(e) => handleChange('employeeCount', e.target.value)}
            className="form-input"
          >
            <option value="">{t('createPage.step1.selectEmployees')}</option>
            {employeeCounts.map(count => (
              <option key={count.value} value={count.value}>{count.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Industry sectors */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          <Tag className="w-4 h-4 inline mr-1" />
          {t('createPage.step1.industrySectors', { count: formData.sectors.length })}
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {sectors.map(sector => (
            <button
              key={sector}
              type="button"
              onClick={() => handleSectorToggle(sector)}
              className={`p-3 text-sm rounded-lg border-2 transition-all duration-200 ${
                formData.sectors.includes(sector)
                  ? 'bg-blue-100 border-blue-400 text-blue-800 shadow-md transform scale-105'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{t(`createPage.step1.sectors.${sector}`)}</span>
                {formData.sectors.includes(sector) && (
                  <Check className="w-4 h-4 text-blue-600" />
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('createPage.step2.title')}</h3>
        <p className="text-gray-600">{t('createPage.step2.description')}</p>
      </div>

      {/* Choix du mode */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button
          onClick={() => handleChange('useAI', true)}
          className={`p-6 rounded-xl border-2 transition-all duration-200 ${
            formData.useAI
              ? 'border-purple-500 bg-purple-50 shadow-lg'
              : 'border-gray-200 hover:border-purple-300'
          }`}
        >
          <div className="text-center">
            <Wand2 className="w-12 h-12 text-purple-600 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('createPage.step2.generateWithAI')}</h4>
            <p className="text-sm text-gray-600">{t('createPage.step2.generateWithAIDesc')}</p>
          </div>
        </button>

        <button
          onClick={() => handleChange('useAI', false)}
          className={`p-6 rounded-xl border-2 transition-all duration-200 ${
            !formData.useAI
              ? 'border-blue-500 bg-blue-50 shadow-lg'
              : 'border-gray-200 hover:border-blue-300'
          }`}
        >
          <div className="text-center">
            <Edit3 className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('createPage.step2.manualFill')}</h4>
            <p className="text-sm text-gray-600">{t('createPage.step2.manualFillDesc')}</p>
          </div>
        </button>
      </div>

      {formData.useAI ? (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
            <div className="flex items-center space-x-3 mb-4">
              <Sparkles className="w-6 h-6 text-purple-600" />
              <h4 className="text-lg font-semibold text-purple-900">{t('createPage.step2.automaticGeneration')}</h4>
            </div>
            <p className="text-purple-800 mb-4">
              {t('createPage.step2.automaticGenerationDesc')}
            </p>
            <button
              onClick={generateAIDescription}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center space-x-2"
            >
              <Wand2 className="w-5 h-5" />
              <span>{t('createPage.step2.generateDescription')}</span>
            </button>
          </div>

          {formData.description && (
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('createPage.step2.generatedDescription')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={8}
                className="form-input"
                placeholder={t('createPage.step2.descriptionWillAppear')}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              {t('createPage.step2.clearDescription')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={8}
              className="form-input"
              placeholder={t('createPage.step2.describePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              {t('createPage.step2.foundingDate')}
            </label>
            <input
              type="date"
              value={formData.foundedDate}
              onChange={(e) => handleChange('foundedDate', e.target.value)}
              className="form-input max-w-xs"
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('createPage.step3.title')}</h3>
        <p className="text-gray-600">{t('createPage.step3.description')}</p>
      </div>

      <div className="space-y-6">
        {formData.solutions.map((solution, index) => (
          <div key={solution.id} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-900">{t('createPage.step3.solutionLabel', { index: index + 1 })}</h4>
              <button
                onClick={() => removeSolution(solution.id)}
                className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Package className="w-4 h-4 inline mr-1" />
                  {t('createPage.step3.solutionName')}
                </label>
                <input
                  type="text"
                  value={solution.name}
                  onChange={(e) => updateSolution(solution.id, 'name', e.target.value)}
                  className="form-input"
                  placeholder={t('createPage.step3.solutionNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Euro className="w-4 h-4 inline mr-1" />
                  {t('createPage.step3.price')}
                </label>
                <input
                  type="text"
                  value={solution.price}
                  onChange={(e) => updateSolution(solution.id, 'price', e.target.value)}
                  className="form-input"
                  placeholder={t('createPage.step3.pricePlaceholder')}
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('createPage.step3.miniPitch')}
              </label>
              <textarea
                value={solution.pitch}
                onChange={(e) => updateSolution(solution.id, 'pitch', e.target.value)}
                rows={3}
                className="form-input"
                placeholder={t('createPage.step3.miniPitchPlaceholder')}
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Tag className="w-4 h-4 inline mr-1" />
                {t('createPage.step3.tagsLabel', { count: solution.tags.length })}
              </label>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                {availableTags.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      const newTags = solution.tags.includes(tag)
                        ? solution.tags.filter(t => t !== tag)
                        : [...solution.tags, tag];
                      updateSolution(solution.id, 'tags', newTags);
                    }}
                    className={`p-2 text-xs rounded-lg border transition-all duration-200 ${
                      solution.tags.includes(tag)
                        ? 'bg-green-100 border-green-300 text-green-800'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}

        <button
          onClick={addSolution}
          className="w-full p-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-colors group"
        >
          <div className="text-center">
            <Plus className="w-8 h-8 text-gray-400 group-hover:text-blue-600 mx-auto mb-2" />
            <span className="text-gray-600 group-hover:text-blue-600 font-medium">
              {t('createPage.step3.addSolution')}
            </span>
          </div>
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('createPage.step4.title')}</h3>
        <p className="text-gray-600">{t('createPage.step4.description')}</p>
      </div>

      {/* Lien Google Reviews */}
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h4 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
          <Globe className="w-5 h-5 mr-2" />
          {t('createPage.step4.googleReviewsLink')}
        </h4>
        <input
          type="url"
          value={formData.googleReviewLink}
          onChange={(e) => handleChange('googleReviewLink', e.target.value)}
          className="form-input"
          placeholder="https://g.page/r/..."
        />
        <p className="text-sm text-blue-700 mt-2">
          {t('createPage.step4.googleReviewsLinkDesc')}
        </p>
      </div>

      {/* Satisfaction rates */}
      <div className="bg-green-50 rounded-xl p-6 border border-green-200">
        <h4 className="text-lg font-semibold text-green-900 mb-6 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2" />
          {t('createPage.step4.satisfactionRate')}
        </h4>

        <div className="space-y-4">
          {Object.entries(formData.satisfactionRates).map(([key, value]) => {
            return (
              <div key={key}>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    {t(`createPage.step4.satisfactionLabels.${key}`)}
                  </label>
                  <span className="text-lg font-bold text-green-700">{value}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={value}
                  onChange={(e) => handleChange('satisfactionRates', {
                    ...formData.satisfactionRates,
                    [key]: parseInt(e.target.value)
                  })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Avis en vedette */}
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Star className="w-5 h-5 mr-2 text-yellow-500" />
          {t('createPage.step4.featuredReviews')}
        </h4>

        <div className="space-y-6">
          {formData.featuredReviews.map((review, index) => (
            <div key={review.id} className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-lg font-semibold text-yellow-900">{t('createPage.step4.reviewLabel', { index: index + 1 })}</h5>
                <button
                  onClick={() => removeReview(review.id)}
                  className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('createPage.step4.author')}</label>
                  <input
                    type="text"
                    value={review.author}
                    onChange={(e) => updateReview(review.id, 'author', e.target.value)}
                    className="form-input"
                    placeholder={t('createPage.step4.authorPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('createPage.step4.company')}</label>
                  <input
                    type="text"
                    value={review.company}
                    onChange={(e) => updateReview(review.id, 'company', e.target.value)}
                    className="form-input"
                    placeholder={t('createPage.step4.companyPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('createPage.step4.productConcerned')}</label>
                  <input
                    type="text"
                    value={review.product}
                    onChange={(e) => updateReview(review.id, 'product', e.target.value)}
                    className="form-input"
                    placeholder={t('createPage.step4.productPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('createPage.step4.rating')}</label>
                  <select
                    value={review.rating}
                    onChange={(e) => updateReview(review.id, 'rating', parseInt(e.target.value))}
                    className="form-input"
                  >
                    {[5, 4, 3, 2, 1].map(rating => (
                      <option key={rating} value={rating}>
                        {t('createPage.step4.star', { count: rating })}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('createPage.step4.comment')}</label>
                <textarea
                  value={review.comment}
                  onChange={(e) => updateReview(review.id, 'comment', e.target.value)}
                  rows={3}
                  className="form-input"
                  placeholder={t('createPage.step4.commentPlaceholder')}
                />
              </div>

              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={review.hasVideo}
                    onChange={(e) => updateReview(review.id, 'hasVideo', e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <Video className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-gray-700">{t('createPage.step4.videoReview')}</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={review.hasImage}
                    onChange={(e) => updateReview(review.id, 'hasImage', e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <ImageIcon className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-gray-700">{t('createPage.step4.withImage')}</span>
                </label>
              </div>
            </div>
          ))}

          <button
            onClick={addFeaturedReview}
            className="w-full p-6 border-2 border-dashed border-yellow-300 rounded-xl hover:border-yellow-400 hover:bg-yellow-50 transition-colors group"
          >
            <div className="text-center">
              <Plus className="w-8 h-8 text-yellow-400 group-hover:text-yellow-600 mx-auto mb-2" />
              <span className="text-yellow-600 group-hover:text-yellow-700 font-medium">
                {t('createPage.step4.addFeaturedReview')}
              </span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.companyName && formData.sectors.length > 0;
      case 2:
        return formData.description.length > 0;
      case 3:
        return formData.solutions.length > 0 && formData.solutions.every(s => s.name && s.pitch);
      case 4:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header avec lien retour */}
        <div className="flex items-center mb-8">
          <Link
            to="/login"
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors mr-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('createPage.backToLogin')}</span>
          </Link>
          <div className="flex-1 text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('createPage.pageTitle')}
            </h1>
            <p className="text-xl text-gray-600">
              {t('createPage.pageSubtitle')}
            </p>
          </div>
        </div>

        {renderStepIndicator()}

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="flex items-center space-x-2 px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>{t('createPage.nav.previous')}</span>
          </button>

          <div className="flex items-center space-x-4">
            {currentStep === 4 && (
              <button
                onClick={handleSubmit}
                className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Eye className="w-5 h-5" />
                <span>{t('createPage.nav.createMyPage')}</span>
              </button>
            )}

            {currentStep < 4 && (
              <button
                onClick={handleNext}
                disabled={!isStepValid()}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{t('createPage.nav.next')}</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateClareefaiPage;